import { useState, useRef } from "react";
import { Drawer, Stack, Typography, IconButton, Box, Paper, Divider, Chip, Avatar, Tooltip, Button, CircularProgress } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Circle, CameraAlt as CameraAltIcon } from "@mui/icons-material";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import AgentPersonalDetails from "./AgentDetailsField";
import AgentPreference from "./AgentPreference";
import CredentialVault from "./CredentialVault/CredentialVault";
import SendPasswordSetupDialog from "./SetNewPasswordDialog";
import ConfirmationModal from "../../../components/confirmation-modal";
import { generateButtonSx } from "../../../utils/elementor-style-override";
import useUpdateAgentStatus from "../../AgentDetails/hooks/useUpdateAgentStatus";

const licenseStatusColor = (status) => {
	switch ((status || "").toLowerCase()) {
		case "active":
			return { bg: "success.verylight", text: "success.dark" };
		case "expired":
			return { bg: "error.verylight", text: "error.dark" };
		case "pending":
			return { bg: "warning.verylight", text: "warning.dark" };
		default:
			return { bg: "secondary.lightbgcolor", text: "text.secondary" };
	}
};

const AgentSlidingPanel = ({
	open,
	onClose,
	setAgents,
	agent,
	setSnackbar,
	updateProfileLoading,
	updateAvatarLoading,
	handleUpdateAgentProfile,
	handleUpdateAgentAvatar,
	handleSendPasswordSetup,
	passwordSetupLoading,
	refetch,
}) => {
	const currentAgent = agent;

	const [openPassDialog, setOpenPassDialog] = useState(false);

	const [confirmation, setConfirmation] = useState({ open: false, action: null });
	const { updateStatus, loading: updateLoading } = useUpdateAgentStatus();

	const [isAvatarHovered, setIsAvatarHovered] = useState(false);
	const avatarInputRef = useRef(null);

	const handleClosePanel = () => {
		onClose();
	};

	const handleAvatarButtonClick = () => {
		if (updateAvatarLoading) return;
		avatarInputRef.current?.click();
	};

	const handleAvatarChange = async (e) => {
		const file = e.target.files?.[0];
		if (!file) return;

		if (!file.type.startsWith("image/")) {
			setSnackbar({ open: true, message: "Please select a valid image file.", severity: "error" });
			if (avatarInputRef.current) avatarInputRef.current.value = "";
			return;
		}

		const previousAvatar = currentAgent?.avatar;
		const previewUrl = URL.createObjectURL(file);

		// optimistic preview while the upload is in flight
		setAgents((prevAgents) => prevAgents.map((a) => (a.user_id === currentAgent.user_id ? { ...a, avatar: previewUrl } : a)));

		const result = await handleUpdateAgentAvatar(currentAgent.user_id, file);

		if (!result?.success) {
			// revert to the last known-good avatar on failure
			setAgents((prevAgents) => prevAgents.map((a) => (a.user_id === currentAgent.user_id ? { ...a, avatar: previousAvatar } : a)));
		}

		if (avatarInputRef.current) avatarInputRef.current.value = "";
	};

	const isActivated = Number(currentAgent?.suspended) === 0;

	const handleAgentStatusToggle = () => {
		setConfirmation({ open: true, action: isActivated ? "deactivate" : "reactivate" });
	};

	const handleCloseConfirmation = () => {
		setConfirmation({ open: false, action: null });
	};

	const handleConfirmAction = async () => {
		try {
			const newStatus = confirmation.action === "deactivate" ? 0 : 1;
			const data = {
				agent_id: currentAgent?.user_id,
				status_to: newStatus,
			};
			const result = await updateStatus(data);

			if (!result?.success) {
				setSnackbar({ open: true, message: "Failed to update agent status. Please try again.", severity: "error" });
				return;
			}

			// The list holds both opted-in and opted-out agents now, so flip the flag
			// rather than dropping the card — the page's toggle decides what is shown.
			// The agent leaves the current tab either way, so close the panel with it.
			setSnackbar({
				open: true,
				message:
					newStatus === 0
						? `${currentAgent?.display_name} opted out. Find them under the Opted Out tab.`
						: `${currentAgent?.display_name} opted back in for service.`,
				severity: "success",
			});

			setAgents((prevAgents) =>
				prevAgents.map((a) => (a.user_id === currentAgent.user_id ? { ...a, suspended: newStatus === 1 ? 0 : 1 } : a))
			);

			handleCloseConfirmation();
			onClose();
			refetch?.();
		} catch (error) {
			console.error(error);
			setSnackbar({ open: true, message: "Unexpected error while updating agent status.", severity: "error" });
		}
	};

	const handleSaveDetails = async (newData) => {
		await handleUpdateAgentProfile(newData);
	};

	const { bg: licenseBg, text: licenseText } = licenseStatusColor(currentAgent?.license_status);

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={(_, reason) => {
				if (reason === "backdropClick" || reason === "escapeKeyDown") return;
				handleClosePanel();
			}}
			slotProps={{
				paper: {
					sx: {
						width: { xs: "100vw !important", sm: "100vw !important", md: "65vw !important" },
						boxShadow: 3,
						display: "flex",
						flexDirection: "column",
					},
				},
			}}
		>
			<Box sx={{ minHeight: "100vh", pt: 7 }}>
				<Stack direction="row" spacing={2.5} px={5} mb={3}>
					<IconButton
						onClick={handleClosePanel}
						size="small"
						sx={{
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 1,
							color: "secondary.main",
							backgroundColor: "transparent !important",
							px: 1,
							"&:hover": {
								backgroundColor: "action.hover",
								borderColor: "primary.main",
								color: "secondary.main",
							},
						}}
					>
						<ArrowBackIcon fontSize="small" />
					</IconButton>

					<Typography variant="h6">Agent Details</Typography>
				</Stack>

				<Box
					sx={{
						position: "relative",
						backgroundColor: "#f7f7f7",
						py: 3,
						px: "40px",
						backgroundImage: ` repeating-radial-gradient(
                                circle at 0 0,
                                transparent 0,
                                #f7f7f7 90px
                            ),
                            repeating-linear-gradient(
                                #ffffffaa,
                                #ffffff
                            )
                        `,
					}}
				>
					<Stack spacing={1} direction="row" alignItems="center" sx={{ position: "absolute", top: 35, right: 40 }}>
						<Circle sx={{ fontSize: 12, color: isActivated ? "success.main" : "error.main" }} />
						<Typography variant="body2" color="text.secondary">
							{isActivated ? "Active" : "Deactivated"}
						</Typography>
					</Stack>

					{/* Avatar — always clickable, camera icon shows on hover */}
					<Box
						sx={{ position: "relative", width: 100, height: 100 }}
						onMouseEnter={() => setIsAvatarHovered(true)}
						onMouseLeave={() => setIsAvatarHovered(false)}
					>
						<Avatar
							src={currentAgent?.avatar || undefined}
							onClick={handleAvatarButtonClick}
							sx={{
								width: 100,
								height: 100,
								bgcolor: "secondary.lightbgcolor",
								border: "2px solid",
								borderColor: "secondary.main",
								fontSize: "1.75rem",
								color: "primary.main",
								cursor: updateAvatarLoading ? "wait" : "pointer",
								filter: !isActivated ? "grayscale(40%) brightness(85%)" : "none",
								opacity: updateAvatarLoading ? 0.6 : 1,
								transition: "filter 0.3s, opacity 0.3s",
							}}
						>
							{currentAgent?.display_name?.charAt(0)?.toUpperCase() || "?"}
						</Avatar>

						{updateAvatarLoading && (
							<CircularProgress
								size={28}
								sx={{ position: "absolute", top: "50%", left: "50%", marginTop: "-14px", marginLeft: "-14px" }}
							/>
						)}

						<input
							ref={avatarInputRef}
							type="file"
							accept="image/*"
							hidden
							onChange={handleAvatarChange}
							disabled={updateAvatarLoading}
						/>

						{isAvatarHovered && !updateAvatarLoading && (
							<Tooltip title="Change photo" arrow>
								<IconButton
									onClick={handleAvatarButtonClick}
									size="small"
									sx={{
										position: "absolute",
										bottom: 0,
										right: 0,
										width: 30,
										height: 30,
										backgroundColor: "primary.main !important",
										border: "2px solid #fff",
										boxShadow: 2,
										"&:hover": { backgroundColor: "primary.dark !important" },
									}}
								>
									<CameraAltIcon sx={{ fontSize: 15, color: "#fff" }} />
								</IconButton>
							</Tooltip>
						)}
					</Box>

					<Typography variant="h5" mt={2} mb={1}>
						{currentAgent?.display_name}
					</Typography>
					<Typography variant="body1" color="text.secondary" mb={1}>
						{currentAgent?.email}
					</Typography>
					{currentAgent?.agency_name && (
						<Typography variant="body2" color="text.secondary" mb={2}>
							{currentAgent.agency_name}
						</Typography>
					)}

					{currentAgent?.license_status && (
						<Chip
							label={currentAgent.license_status}
							size="small"
							sx={{
								backgroundColor: licenseBg,
								color: licenseText,
								fontWeight: 500,
								fontSize: "0.75rem",
							}}
						/>
					)}

					{/* Activate / Deactivate — bottom right of this box */}
					<Box sx={{ position: "absolute", bottom: 30, right: 40 }}>
						<Button
							size="small"
							variant="contained"
							startIcon={isActivated ? <PersonOffIcon /> : <RestartAltIcon />}
							onClick={handleAgentStatusToggle}
							sx={{
								fontWeight: 700,
								px: 4,
								gap: 0.5,
								color: isActivated ? "#DC2626 !important" : "#16A34A !important",
								backgroundColor: isActivated ? "#FEF2F2 !important" : "#ECFDF5 !important",
								...generateButtonSx(
									isActivated ? { bg: "#FEF2F2 !important", text: "#DC2626" } : { bg: "#ECFDF5 !important", text: "#16A34A" },
									isActivated ? { bg: "#FEE2E2 !important", text: "#DC2626" } : { bg: "#D1FAE5 !important", text: "#16A34A" }
								),
							}}
						>
							{isActivated ? "Deactivate Agent" : "Activate Agent"}
						</Button>
					</Box>
				</Box>

				<Stack direction={{ xs: "column", md: "row" }} gap={4} px={{ xs: 2, md: 5 }} py={3}>
					{/* Personal Details  */}
					<Box flex={1} sx={{ width: "100% !important" }}>
						<AgentPersonalDetails
							currentAgent={currentAgent}
							onSaveDetails={handleSaveDetails}
							updateProfileLoading={updateProfileLoading}
						/>
					</Box>

					{/* Security Settings + Agent Preference */}
					<Stack flex={0.8} spacing={4} sx={{ width: "100% !important" }}>
						<Paper variant="outlined" sx={{ borderRadius: 2, width: "100% !important" }}>
							<Box px={2} py={1.5} sx={{ backgroundColor: "secondary.lightbgcolor" }}>
								<Typography fontWeight={600} color="text.primary">
									Security Settings
								</Typography>
							</Box>
							<Divider />
							<Stack spacing={1.5} py={1.5}>
								<Box
									onClick={() => setOpenPassDialog(true)}
									sx={{
										display: "inline-block",
										color: "primary.main",
										cursor: "pointer",
										textDecoration: "underline",
										"&:hover": { textDecoration: "underline" },
										fontWeight: 500,
										px: 2,
									}}
								>
									<Typography variant="body2">Set new password</Typography>
								</Box>
							</Stack>
						</Paper>

						<AgentPreference currentAgent={currentAgent} />
					</Stack>
				</Stack>

				<Box px={{ xs: 2, md: 5 }} pb={3}>
					<CredentialVault key={currentAgent?.user_id} currentAgent={currentAgent} />
				</Box>
			</Box>

			<SendPasswordSetupDialog
				open={openPassDialog}
				onClose={() => setOpenPassDialog(false)}
				onConfirm={handleSendPasswordSetup}
				loading={passwordSetupLoading}
				userName={currentAgent?.display_name}
				userEmail={currentAgent?.email}
			/>

			<ConfirmationModal
				open={confirmation.open}
				onClose={handleCloseConfirmation}
				onConfirm={handleConfirmAction}
				isLoading={updateLoading}
				title={confirmation.action === "deactivate" ? "Deactivate Agent?" : "Activate Agent?"}
				description={
					confirmation.action === "deactivate"
						? `${currentAgent?.display_name} will be removed from the portal — hidden from every agent picker, blocked from signing in, and excluded from all commission computations, so their own earnings drop to $0 and their book stops counting toward their upline's override. Their book of business and hierarchy are kept, and downlines keep their levels and their own computations. Closed cutoffs are not affected. You can find them again under the Opted Out tab here and opt them back in at any time.`
						: `${currentAgent?.display_name}'s access to the portal will be restored and they will be included in commission computations again. The current cutoff is recomputed from their book of business. Note this takes effect now but is not permanent: the next import re-applies whatever that agent's Opted For Service column says, so if the spreadsheet reads false, update it too or this will be reverted.`
				}
				confirmText={confirmation.action === "deactivate" ? "Deactivate Agent" : "Activate Agent"}
				confirmColor={confirmation.action === "deactivate" ? "#DC2626 !important" : "#16A34A !important"}
			/>
		</Drawer>
	);
};
export default AgentSlidingPanel;
